import axios from 'axios';

// Base API configuration
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.citycourier.org/api/v1';

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface BankAccount {
  id: number;
  title: string;
  bank_name: string;
  number: string;
  iban: string;
  primary: boolean;
}

export interface AreaZone {
  code: string;
  name: string;
}

export interface PointOfSale {
  code: string;
  name: string;
}

export interface Branch {
  name: string;
}

export interface ClientProfile {
  account_no: string;
  areaZone?: AreaZone;
  pointOfSale?: PointOfSale;
  account_type: string;
  business_registration_status: string;
  company_name: string;
  address: string;
  web_address: string;
  brand_name: string;
  business_sector: string;
  products: string;
  authorized_person_name: string;
  authorized_designation: string;
  authorized_phone: string;
  authorized_cnic: string;
  admin_contact_name: string;
  admin_phone: string;
  finance_contact_name: string;
  ntn: string;
  gst: string;
  pickup_address: string;
  pickup_contact_person: string;
  pickup_phone: string;
  pickup_email: string;
  invoicing?: string;
}

export interface ProfileUpdateData {
  // User fields
  name?: string;
  // Client profile fields
  company_name?: string;
  address?: string;
  web_address?: string;
  brand_name?: string;
  business_sector?: string;
  products?: string;
  authorized_person_name?: string;
  authorized_designation?: string;
  authorized_phone?: string;
  authorized_cnic?: string;
  admin_contact_name?: string;
  admin_phone?: string;
  finance_contact_name?: string;
  ntn?: string;
  gst?: string;
  pickup_address?: string;
  pickup_contact_person?: string;
  pickup_phone?: string;
  pickup_email?: string;
  invoicing?: string;
  // Bank account fields
  bank_account_title?: string;
  bank_name?: string;
  bank_iban?: string;
  bank_account_number?: string;
}

export interface User {
  id: number;
  name: string;
  loginID: string;
  email: string;
  status: number;
  created_at: string;
  branch?: Branch;
  clientProfile: ClientProfile;
  bankAccounts: BankAccount[];
}

export interface ApiResponse {
  data: {
    user: User;
  };
}

export interface ChangePasswordData {
  current_password: string;
  new_password: string;
  new_password_confirmation: string;
}

// Transform API response to match frontend expectations (snake_case to camelCase for relationships)
const transformUserResponse = (apiUser: any): User => {
  return {
    ...apiUser,
    clientProfile: apiUser.client_profile ? {
      ...apiUser.client_profile,
      areaZone: apiUser.client_profile.area_zone,
      pointOfSale: apiUser.client_profile.point_of_sale,
    } : undefined,
    bankAccounts: apiUser.bank_accounts || [],
  };
};

// Profile API functions
export const profileApi = {
  // Get user profile
  getProfile: async (): Promise<ApiResponse> => {
    const response = await apiClient.get('/me');
    const transformedUser = transformUserResponse(response.data.data.user);
    return {
      data: {
        user: transformedUser
      }
    };
  },

  // Change password
  changePassword: async (data: ChangePasswordData): Promise<any> => {
    const response = await apiClient.post('/change-password', data);
    return response.data;
  },

  // Update profile
  updateProfile: async (data: ProfileUpdateData): Promise<any> => {
    const response = await apiClient.put('/profile', data);
    return response.data;
  },

  // Get bank accounts
  getBankAccounts: async (): Promise<{ data: BankAccount[] }> => {
    const response = await apiClient.get('/bank-accounts');
    return response.data;
  },

  // Add bank account
  addBankAccount: async (data: Omit<BankAccount, 'id'>): Promise<any> => {
    const response = await apiClient.post('/bank-accounts', data);
    return response.data;
  },

  // Update bank account
  updateBankAccount: async (id: number, data: Partial<BankAccount>): Promise<any> => {
    const response = await apiClient.put(`/bank-accounts/${id}`, data);
    return response.data;
  },

  // Delete bank account
  deleteBankAccount: async (id: number): Promise<any> => {
    const response = await apiClient.delete(`/bank-accounts/${id}`);
    return response.data;
  },
};

export default profileApi; 